import { useState } from 'react'
import { Modal } from '@/components/Modal'
import { SearchableSelect } from '@/components/SearchableSelect'
import { optionsFrom, useOrganizationTypes } from './queries'
import type { OrganizationRow } from './types'

/**
 * Port of Views/Profile/Information/_Organization.cshtml + _organization.js.
 *
 * Optional, like work experience. `OrganizationPosition` is the hardcoded
 * 1 = Leader / 2 = Member pair from the Razor's inline <option>s, and it goes
 * upstream as an integer.
 */
interface Props {
  rows: OrganizationRow[]
  onChange: (rows: OrganizationRow[]) => void
  /** The "Add" button lives in the section header, which the page owns. */
  isModalOpen: boolean
  onCloseModal: () => void
}

const POSITIONS = [
  { value: '1', label: 'Leader' },
  { value: '2', label: 'Member' },
]

const EMPTY: OrganizationRow = {
  OrganizationName: '',
  mstOrganizationTypeID: '',
  OrganizationTypeName: '',
  OrganizationPosition: '',
}

export function OrganizationSection({
  rows,
  onChange,
  isModalOpen,
  onCloseModal,
}: Props): JSX.Element {
  const [draft, setDraft] = useState<OrganizationRow>(EMPTY)
  const [error, setError] = useState('')
  const types = useOrganizationTypes()

  function add(): void {
    if (!draft.OrganizationName.trim()) return setError('Please enter the organization name.')
    if (!draft.OrganizationPosition) return setError('Please choose your position.')
    setError('')
    onChange([...rows, draft])
    setDraft(EMPTY)
    onCloseModal()
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover" id="tblOrganization">
          <thead>
            <tr>
              <th className="text-center">#</th>
              <th className="text-center">Organization</th>
              <th className="text-center">Type</th>
              <th className="text-center">Position</th>
              <th className="text-center" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  No organization added yet
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.OrganizationName}-${index}`}>
                  <td className="text-center">{index + 1}</td>
                  <td>{row.OrganizationName}</td>
                  <td>{row.OrganizationTypeName}</td>
                  <td>
                    {POSITIONS.find((p) => p.value === row.OrganizationPosition)?.label ?? ''}
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger"
                      onClick={() => onChange(rows.filter((_, i) => i !== index))}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        id="organizationModal"
        title="Add Organization"
        isOpen={isModalOpen}
        onClose={onCloseModal}
      >
      <form className="needs-validation" id="addOrganizationInfo" noValidate>
        <div className="mb-3">
          <label className="form-label">Organization Name</label>
          <input
            type="text"
            className="form-control"
            id="txtOrganizationName"
            maxLength={100}
            placeholder="Organization Name"
            value={draft.OrganizationName}
            onChange={(e) => setDraft({ ...draft, OrganizationName: e.target.value })}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Organization Type</label>
          <SearchableSelect
            id="slsOrganizationType"
            options={optionsFrom(
              types.data?.Data,
              (t) => t.mstOrganizationTypeID,
              (t) => t.OrganizationType,
            )}
            value={draft.mstOrganizationTypeID}
            onChange={(id) =>
              setDraft({
                ...draft,
                mstOrganizationTypeID: id,
                OrganizationTypeName:
                  types.data?.Data?.find((t) => String(t.mstOrganizationTypeID) === id)
                    ?.OrganizationType ?? '',
              })
            }
            placeholder="Select organization type"
            loading={types.isLoading}
            emptyLabel={null}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Position in that Organization</label>
          <select
            id="slsOrganisationPosition"
            className="form-select"
            value={draft.OrganizationPosition}
            onChange={(e) => setDraft({ ...draft, OrganizationPosition: e.target.value })}
          >
            <option value="" disabled>
              Select Position
            </option>
            {POSITIONS.map((position) => (
              <option key={position.value} value={position.value}>
                {position.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex justify-content-end">
          <button
            type="button"
            id="btnAddOrganization"
            className="btn btn-secondary px-4"
            onClick={add}
          >
            Add Organization
          </button>
        </div>
      </form>
      </Modal>
    </>
  )
}
